import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { 
  Search, Globe, TrendingUp, 
  Target, Download, RefreshCw, ChevronDown, ChevronUp, Plus, CheckSquare, Square, Upload, X, Check, AlertCircle
} from 'lucide-react';
import { formatNumber, formatCurrency } from '../utils/formatting';
import { useAuth } from '@clerk/clerk-react';
import { trackOnboardingMilestone } from '../utils/onboarding';
const API_URL = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api`;

export default function KeywordResearch({ onCreateCampaign }) {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [seedKeyword, setSeedKeyword] = useState('');
  const [landingPageUrl, setLandingPageUrl] = useState('');
  const [urlMode, setUrlMode] = useState('keywords');
  const [multiLangResults, setMultiLangResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('nl');
  const [expandedLanguages, setExpandedLanguages] = useState({});
  const [selectedKeywords, setSelectedKeywords] = useState({});
  const [keywordMatchTypes, setKeywordMatchTypes] = useState({});
  const [defaultMatchType, setDefaultMatchType] = useState('BROAD');
  const [selectedLanguages, setSelectedLanguages] = useState(['nl']);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [tempSelectedLanguages, setTempSelectedLanguages] = useState(['nl']);
  const [seedKeywordLanguage, setSeedKeywordLanguage] = useState('en');
  const [existingKeywords, setExistingKeywords] = useState({});
  const [showAddToExistingModal, setShowAddToExistingModal] = useState(false);
  const [addToExistingLang, setAddToExistingLang] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [adGroups, setAdGroups] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [selectedAdGroupId, setSelectedAdGroupId] = useState('');
  const [selectedMatchType, setSelectedMatchType] = useState('BROAD');
  const [addingKeywords, setAddingKeywords] = useState(false);
  const [addResult, setAddResult] = useState(null);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [adGroupsLoading, setAdGroupsLoading] = useState(false);
  const [campaignSearch, setCampaignSearch] = useState('');
  // Structure: { 'nl': [{ keyword: 'test', volume: 1000, cpc: 1.5, competition: 'LOW' }], 'es': [...] }

  // All available languages for multi-language research
  const allLanguages = [
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱', color: '#ff6b35', popular: true },
    { code: 'en', name: 'English', flag: '🇺🇸', color: '#3b82f6', popular: true },
    { code: 'es', name: 'Español', flag: '🇪🇸', color: '#ffd23f', popular: true },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪', color: '#3b82f6', popular: true },
    { code: 'fr', name: 'Français', flag: '🇫🇷', color: '#8b5cf6', popular: true },
    { code: 'it', name: 'Italiano', flag: '🇮🇹', color: '#10b981', popular: true },
    { code: 'pt', name: 'Português', flag: '🇵🇹', color: '#10b981', popular: false },
    { code: 'sv', name: 'Svenska', flag: '🇸🇪', color: '#3b82f6', popular: false },
    { code: 'no', name: 'Norsk', flag: '🇳🇴', color: '#ef4444', popular: false },
    { code: 'fi', name: 'Suomi', flag: '🇫🇮', color: '#3b82f6', popular: false },
    { code: 'pl', name: 'Polski', flag: '🇵🇱', color: '#ef4444', popular: false },
    { code: 'cs', name: 'Čeština', flag: '🇨🇿', color: '#3b82f6', popular: false },
    { code: 'da', name: 'Dansk', flag: '🇩🇰', color: '#ec4899', popular: false },
    { code: 'ro', name: 'Română', flag: '🇷🇴', color: '#ffd23f', popular: false },
    { code: 'el', name: 'Ελληνικά', flag: '🇬🇷', color: '#3b82f6', popular: false },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷', color: '#ef4444', popular: false },
    { code: 'hu', name: 'Magyar', flag: '🇭🇺', color: '#10b981', popular: false }
  ];

  const openLanguageModal = () => {
    setTempSelectedLanguages([...selectedLanguages]);
    setShowLanguageModal(true);
  };

  const closeLanguageModal = () => {
    setShowLanguageModal(false);
  };

  const applyLanguageSelection = () => {
    setSelectedLanguages([...tempSelectedLanguages]);
    setShowLanguageModal(false);
  };

  const toggleTempLanguageSelection = (langCode) => {
    setTempSelectedLanguages(prev => {
      if (prev.includes(langCode)) {
        // Must have at least one language
        if (prev.length === 1) return prev;
        return prev.filter(code => code !== langCode);
      } else {
        return [...prev, langCode];
      }
    });
  };

  const toggleAllTempLanguages = () => {
    if (tempSelectedLanguages.length === allLanguages.length) {
      // Deselect all except first
      setTempSelectedLanguages([allLanguages[0].code]);
    } else {
      // Select all
      setTempSelectedLanguages(allLanguages.map(l => l.code));
    }
  };

  const searchMultiLanguage = async () => {
    console.log('🔍 searchMultiLanguage called', { seedKeyword, landingPageUrl, urlMode });
    
    if (!seedKeyword && !landingPageUrl) {
      alert('Please enter a keyword or URL');
      return;
    }

    if (selectedLanguages.length === 0) {
      alert('Please select at least one language');
      return;
    }
    
    // Mark the "Try Keyword Research" onboarding milestone as completed.
    // Fired here (and not on mount) so the user genuinely tried the tool.
    trackOnboardingMilestone('try_keyword_research');

    setLoading(true);
    setMultiLangResults(null); // Clear previous results
    
    // Automatically include the seed keyword language in the research
    // This ensures users get results in the original language without manually selecting it
    const languagesToResearch = selectedLanguages.includes(seedKeywordLanguage)
      ? selectedLanguages
      : [seedKeywordLanguage, ...selectedLanguages];
    
    try {
      console.log('📡 Sending request to:', `${API_URL}/keywords/research-multilang`);
      console.log('📦 Payload:', { seedKeyword, seedKeywordLanguage, landingPageUrl, urlMode, languages: languagesToResearch });
      
      // Get auth token
      const token = await getToken();
      
      // Get selected account from localStorage
      const savedAccount = localStorage.getItem('selectedGoogleAdsAccount');
      let accountId = null;
      if (savedAccount) {
        try {
          const account = JSON.parse(savedAccount);
          accountId = account.id;
          console.log('📊 Using account:', accountId);
        } catch (e) {
          console.error('Failed to parse saved account:', e);
        }
      }
      
      if (!accountId) {
        alert('Please select a Google Ads account first');
        setLoading(false);
        return;
      }
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 min timeout
      
      const response = await fetch(`${API_URL}/keywords/research-multilang`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Account-Id': accountId
        },
        body: JSON.stringify({ 
          seedKeyword: seedKeyword || undefined,
          seedKeywordLanguage: seedKeywordLanguage,
          landingPageUrl: landingPageUrl || undefined,
          urlMode: urlMode,
          languages: languagesToResearch
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      console.log('📥 Response status:', response.status);
      const data = await response.json();
      console.log('📊 Response data:', data);
      
      if (data.success) {
        setMultiLangResults(data);
        // Expand all languages by default (including auto-added seed language)
        const expanded = {};
        languagesToResearch.forEach(langCode => {
          expanded[langCode] = true;
        });
        setExpandedLanguages(expanded);
        // Check which keywords already exist in the account
        checkExistingKeywords(data);
        console.log('✅ Results set successfully');
      } else {
        console.error('❌ API returned success: false', data);
        alert('Failed to fetch keywords. Check console for details.');
      }
    } catch (error) {
      console.error('❌ Error researching keywords:', error);
      alert(`Error: ${error.message}. Check console for details.`);
    } finally {
      setLoading(false);
      console.log('🏁 Loading complete');
    }
  };

  const getCompetitionColor = (competition) => {
    switch(competition) {
      case 'LOW': return '#10b981';
      case 'MEDIUM': return '#f59e0b';
      case 'HIGH': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const exportAllKeywords = () => {
    if (!multiLangResults) return;

    const rows = [['Language', 'Keyword', 'Search Volume', 'CPC', 'Competition']];
    
    Object.entries(multiLangResults.data).forEach(([langCode, langData]) => {
      langData.keywords.forEach(kw => {
        rows.push([
          langData.language,
          kw.keyword,
          kw.volume,
          kw.cpc,
          kw.competition
        ]);
      });
    });

    const csv = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `keywords-multilang-${seedKeyword}.csv`;
    a.click();
  };

  const toggleLanguageExpand = (langCode) => {
    setExpandedLanguages(prev => ({
      ...prev,
      [langCode]: !prev[langCode]
    }));
  };

  const toggleKeywordSelection = (langCode, keyword) => {
    setSelectedKeywords(prev => {
      const langSelected = prev[langCode] || [];
      const isSelected = langSelected.some(k => k.keyword === keyword.keyword);
      
      if (isSelected) {
        return {
          ...prev,
          [langCode]: langSelected.filter(k => k.keyword !== keyword.keyword)
        };
      } else {
        return {
          ...prev,
          [langCode]: [...langSelected, keyword]
        };
      }
    });
  };

  const toggleSelectAll = (langCode, keywords) => {
    setSelectedKeywords(prev => {
      const langSelected = prev[langCode] || [];
      
      if (langSelected.length === keywords.length) {
        // Deselect all
        return { ...prev, [langCode]: [] };
      } else {
        // Select all
        return { ...prev, [langCode]: [...keywords] };
      }
    });
  };

  const isKeywordSelected = (langCode, keyword) => {
    const langSelected = selectedKeywords[langCode] || [];
    return langSelected.some(k => k.keyword === keyword.keyword);
  };

  const areAllSelected = (langCode, keywords) => {
    const langSelected = selectedKeywords[langCode] || [];
    return keywords.length > 0 && langSelected.length === keywords.length;
  };

  const getSelectedCount = (langCode) => {
    return (selectedKeywords[langCode] || []).length;
  };

  const getTotalSelectedCount = () => {
    return Object.values(selectedKeywords).reduce((sum, keywords) => sum + keywords.length, 0);
  };

  const getMatchType = (langCode, keywordText) => {
    return keywordMatchTypes[`${langCode}_${keywordText}`] || defaultMatchType;
  };

  const setMatchType = (langCode, keywordText, matchType) => {
    setKeywordMatchTypes(prev => ({
      ...prev,
      [`${langCode}_${keywordText}`]: matchType
    }));
  };

  const clearSelection = () => {
    setSelectedKeywords({});
    setKeywordMatchTypes({});
  };

  const handleCreateCampaign = (langCode) => {
    const keywords = selectedKeywords[langCode] || [];
    if (keywords.length === 0) return;
    
    const langData = multiLangResults.data[langCode];
    const keywordsWithMatchType = keywords.map(kw => ({
      ...kw,
      matchType: getMatchType(langCode, kw.keyword)
    }));

    if (onCreateCampaign) {
      onCreateCampaign({
        language: langCode,
        languageName: langData.language,
        keywords: keywordsWithMatchType,
        country: langData.country,
        seedKeyword: langData.translatedSeed || seedKeyword
      });
    }
  };

  // Check which keywords already exist in the account
  const checkExistingKeywords = async (results) => {
    try {
      const allKeywords = [];
      Object.values(results.data).forEach(langData => {
        langData.keywords.forEach(kw => {
          if (kw.keyword && !allKeywords.includes(kw.keyword.toLowerCase())) {
            allKeywords.push(kw.keyword.toLowerCase());
          }
        });
      });

      if (allKeywords.length === 0) return;

      const response = await authFetch('/keywords/check-existing', {
        method: 'POST',
        body: JSON.stringify({ keywords: allKeywords })
      });
      const data = await response.json();
      if (data.success && data.existing) {
        setExistingKeywords(data.existing);
      }
    } catch (e) {
      console.error('Check existing keywords error:', e);
    }
  };

  const getExistingInfo = (keyword) => {
    const lower = (keyword || '').toLowerCase();
    return existingKeywords[lower] || null;
  };

  // Helper to make authenticated fetch calls (consistent with the rest of this component)
  const authFetch = async (url, options = {}) => {
    const token = await getToken();
    const savedAccount = localStorage.getItem('selectedGoogleAdsAccount');
    let accountId = '';
    if (savedAccount) {
      try { accountId = JSON.parse(savedAccount).id; } catch (e) {}
    }
    const separator = url.includes('?') ? '&' : '?';
    const fullUrl = `${API_URL}${url}${separator}accountId=${accountId}`;
    return fetch(fullUrl, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Account-Id': accountId,
        ...(options.headers || {})
      }
    });
  };

  // Add to Existing Campaign modal
  const openAddToExisting = async (langCode) => {
    setAddToExistingLang(langCode);
    setSelectedCampaignId('');
    setSelectedAdGroupId('');
    setSelectedMatchType('BROAD');
    setAdGroups([]);
    setAddResult(null);
    setShowAddToExistingModal(true);

    setCampaignsLoading(true);
    try {
      const response = await authFetch('/campaigns/list');
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.data || []);
      } else {
        setAddResult({ success: false, message: data.error || 'Failed to load campaigns' });
      }
    } catch (e) {
      console.error('Fetch campaigns error:', e);
      setAddResult({ success: false, message: 'Failed to load campaigns' });
    }
    setCampaignsLoading(false);
  };

  const handleCampaignSelect = async (campaignId) => {
    setSelectedCampaignId(campaignId);
    setSelectedAdGroupId('');
    setAdGroups([]);
    if (!campaignId) return;

    setAdGroupsLoading(true);
    try {
      const response = await authFetch(`/campaigns/${campaignId}/adgroups`);
      const data = await response.json();
      if (data.success) {
        setAdGroups(data.data || data.adGroups || []);
      }
    } catch (e) {
      console.error('Fetch ad groups error:', e);
    }
    setAdGroupsLoading(false);
  };

  const handleAddKeywords = async () => {
    if (!selectedCampaignId || !selectedAdGroupId || !addToExistingLang) return;
    const keywords = selectedKeywords[addToExistingLang] || [];
    if (keywords.length === 0) return;

    setAddingKeywords(true);
    setAddResult(null);
    try {
      const response = await authFetch(`/campaigns/${selectedCampaignId}/adgroups/${selectedAdGroupId}/keywords`, {
        method: 'POST',
        body: JSON.stringify({
          keywords: keywords.map(kw => ({
            keyword: kw.keyword,
            matchType: selectedMatchType
          }))
        })
      });
      const data = await response.json();
      if (data.success) {
        setAddResult({ success: true, message: `${keywords.length} keywords added successfully` });
        if (multiLangResults) checkExistingKeywords(multiLangResults);
      } else {
        setAddResult({ success: false, message: data.error || 'Failed to add keywords' });
      }
    } catch (e) {
      setAddResult({ success: false, message: e.message });
    }
    setAddingKeywords(false);
  };

  const getTotalKeywords = () => {
    if (!multiLangResults) return 0;
    return Object.values(multiLangResults.data).reduce((sum, lang) => sum + lang.keywords.length, 0);
  };

  return (
    <div style={{ padding: 40 }}>
      {/* Header */}
      <div style={{ marginBottom: 40 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <Globe size={36} color="#10b981" />
          <h1 style={{ color: theme.colors.text.primary, fontSize: 32, fontWeight: 700, margin: 0 }}>
            Multi-Language Keyword Research
          </h1>
        </div>
        <p style={{ color: theme.colors.text.tertiary, fontSize: 16 }}>
          Research keywords in up to 17 languages simultaneously - Select your target markets below
        </p>
      </div>

      {/* Search Section */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 20,
        padding: 30,
        marginBottom: 40,
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Research Mode Selector */}
        <div style={{ marginBottom: 25 }}>
          <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 14, display: 'block', marginBottom: 12, fontWeight: 500 }}>
            Research Mode
          </label>
          <div style={{ display: 'flex', gap: 12 }}>
            {[
              { value: 'keywords', label: '🔤 Keywords Only', desc: 'Research based on keywords' },
              { value: 'url', label: '🌐 URL Only', desc: 'Analyze landing page or competitor' },
              { value: 'both', label: '✨ Keywords + URL', desc: 'Combined for best results' }
            ].map(mode => (
              <button
                key={mode.value}
                onClick={() => setUrlMode(mode.value)}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  background: urlMode === mode.value 
                    ? 'rgba(16, 185, 129, 0.2)' 
                    : 'rgba(255, 255, 255, 0.03)',
                  border: `2px solid ${urlMode === mode.value ? '#10b981' : 'rgba(255, 255, 255, 0.1)'}`,
                  borderRadius: 12,
                  color: urlMode === mode.value ? '#10b981' : 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textAlign: 'left'
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 14 }}>{mode.label}</div>
                <div style={{ fontSize: 11, opacity: 0.8 }}>{mode.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Seed Keyword Input - FIRST */}
        {(urlMode === 'keywords' || urlMode === 'both') && (
          <div style={{ marginBottom: 25 }}>
            <label style={{ color: theme.colors.text.secondary, fontSize: 14, display: 'block', marginBottom: 4, fontWeight: 500 }}>
              Seed Keyword
            </label>
            <p style={{ 
              color: theme.colors.text.tertiary, 
              fontSize: 12, 
              margin: '0 0 8px 0',
              lineHeight: 1.4
            }}>
              Enter your keyword and select its language. We'll translate it to your target languages.
            </p>
            <div style={{ display: 'flex', gap: 16, maxWidth: 800 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search size={20} color={theme.colors.text.tertiary} style={{ 
                  position: 'absolute', 
                  left: 15, 
                  top: '50%', 
                  transform: 'translateY(-50%)'
                }} />
                <input
                  type="text"
                  value={seedKeyword}
                  onChange={(e) => setSeedKeyword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchMultiLanguage()}
                  placeholder="e.g. laptop, sneakers, insurance..."
                  style={{
                    width: '100%',
                    padding: '14px 14px 14px 45px',
                    background: theme.colors.bg.secondary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: 12,
                    color: theme.colors.text.primary,
                    fontSize: 16
                  }}
                />
              </div>
              {/* Seed Keyword Language Selector */}
              <div style={{ position: 'relative', width: 150, flexShrink: 0 }}>
                <select
                  value={seedKeywordLanguage}
                  onChange={(e) => setSeedKeywordLanguage(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 12px',
                    paddingRight: 32,
                    background: theme.colors.bg.secondary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: 12,
                    color: theme.colors.text.primary,
                    fontSize: 14,
                    cursor: 'pointer',
                    appearance: 'none',
                    WebkitAppearance: 'none'
                  }}
                >
                  {allLanguages.map(lang => (
                    <option key={lang.code} value={lang.code} style={{ background: theme.colors.bg.secondary, color: theme.colors.text.primary }}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
                <ChevronDown size={16} color={theme.colors.text.tertiary} style={{ 
                  position: 'absolute', 
                  right: 12, 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none'
                }} />
              </div>
            </div>
          </div>
        )}

        {/* Target Languages Selection - SECOND */}
        <div style={{ marginBottom: 25 }}>
          <label style={{ color: theme.colors.text.secondary, fontSize: 14, display: 'block', marginBottom: 12, fontWeight: 500 }}>
            Target Languages
          </label>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Selected languages as badges */}
            {allLanguages.filter(lang => selectedLanguages.includes(lang.code)).map(lang => (
              <div
                key={lang.code}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 12px',
                  background: 'rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: 10,
                  color: '#10b981',
                  fontSize: 13,
                  fontWeight: 500
                }}
              >
                <span style={{ fontSize: 16 }}>{lang.flag}</span>
                <span>{lang.name}</span>
              </div>
            ))}
            
            {/* Select Languages Button */}
            <button
              onClick={openLanguageModal}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 16px',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '2px dashed rgba(59, 130, 246, 0.4)',
                borderRadius: 10,
                color: '#3b82f6',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <Globe size={16} />
              Select Languages ({selectedLanguages.length})
            </button>
          </div>
        </div>

        {/* URL Input - THIRD (if applicable) */}
        {(urlMode === 'url' || urlMode === 'both') && (
          <div style={{ marginBottom: 25 }}>
            <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 14, display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Landing Page / Competitor URL {urlMode === 'both' ? '(Optional)' : ''}
            </label>
            <div style={{ position: 'relative' }}>
              <Globe size={20} style={{ 
                position: 'absolute', 
                left: 15, 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: 'rgba(255, 255, 255, 0.4)'
              }} />
              <input
                type="url"
                value={landingPageUrl}
                onChange={(e) => setLandingPageUrl(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchMultiLanguage()}
                placeholder="https://example.com/product"
                style={{
                  width: '100%',
                  padding: '14px 14px 14px 45px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 12,
                  color: 'white',
                  fontSize: 16
                }}
              />
            </div>
          </div>
        )}

        {/* Research Button */}
        <button
          onClick={searchMultiLanguage}
          disabled={loading || (!seedKeyword && !landingPageUrl)}
          style={{
            width: '100%',
            background: loading || (!seedKeyword && !landingPageUrl)
              ? 'rgba(16, 185, 129, 0.3)'
              : 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
            border: 'none',
            color: 'white',
            padding: '16px 40px',
            borderRadius: 12,
            cursor: loading || (!seedKeyword && !landingPageUrl) ? 'not-allowed' : 'pointer',
            fontSize: 16,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            boxShadow: loading || (!seedKeyword && !landingPageUrl) ? 'none' : '0 4px 12px rgba(16, 185, 129, 0.3)'
          }}
        >
          {loading ? (
            <>
              <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
              Researching {selectedLanguages.length} language{selectedLanguages.length !== 1 ? 's' : ''}...
            </>
          ) : (
            <>
              <Search size={20} />
              Research Keywords
            </>
          )}
        </button>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Results */}
      {multiLangResults && (
        <div>
          {/* Summary Bar */}
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
            borderRadius: 16,
            padding: 20,
            marginBottom: 30,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, margin: 0, marginBottom: 8 }}>
                Research Complete! 🎉
              </h3>
              <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: 14, margin: 0, marginBottom: 8 }}>
                Found {getTotalKeywords()} keywords across {Object.keys(multiLangResults.data).length} languages
                {multiLangResults.seedKeyword && ` for "${multiLangResults.seedKeyword}"`}
                {multiLangResults.landingPageUrl && ` from URL analysis`}
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {/* Data Source Badge — counts real vs AI across all returned languages */}
                {(() => {
                  const langs = Object.values(multiLangResults.data || {});
                  if (langs.length === 0) return null;
                  const realCount = langs.filter(l => l.realData || l.method === 'keyword_ideas_api' || l.method === 'cache').length;
                  const aiCount = langs.length - realCount;
                  if (realCount === langs.length) {
                    return (
                      <span style={{ background: 'rgba(59, 130, 246, 0.3)', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, color: 'white' }}>
                        🎯 Google Ads API ({realCount}/{langs.length})
                      </span>
                    );
                  }
                  if (aiCount === langs.length) {
                    return (
                      <span style={{ background: 'rgba(139, 92, 246, 0.3)', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, color: 'white' }}>
                        🤖 AI Generated ({aiCount}/{langs.length})
                      </span>
                    );
                  }
                  return (
                    <>
                      <span style={{ background: 'rgba(59, 130, 246, 0.3)', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, color: 'white' }}>
                        🎯 Google Ads API ({realCount}/{langs.length})
                      </span>
                      <span style={{ background: 'rgba(139, 92, 246, 0.3)', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, color: 'white' }}>
                        🤖 AI ({aiCount}/{langs.length})
                      </span>
                    </>
                  );
                })()}
                {/* URL Mode Badge */}
                {multiLangResults.urlMode && (
                  <span style={{
                    background: 'rgba(16, 185, 129, 0.3)',
                    padding: '4px 10px',
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'white'
                  }}>
                    {multiLangResults.urlMode === 'keywords' && '🔤 Keywords'}
                    {multiLangResults.urlMode === 'url' && '🌐 URL Analysis'}
                    {multiLangResults.urlMode === 'both' && '✨ Combined'}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={exportAllKeywords}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: 10,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <Download size={18} />
              Export All
            </button>
          </div>

          {/* Language Results */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Show all languages that have results (includes auto-added seed language) */}
            {allLanguages.filter(lang => multiLangResults.data[lang.code]).map((lang) => {
              const langData = multiLangResults.data[lang.code];
              if (!langData) return null;

              const isExpanded = expandedLanguages[lang.code];

              return (
                <div key={lang.code} style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 20,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  overflow: 'hidden'
                }}>
                  {/* Language Header */}
                  <div 
                    onClick={() => toggleLanguageExpand(lang.code)}
                    style={{
                      padding: 20,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      background: isExpanded ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: lang.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 24
                      }}>
                        {lang.flag}
                      </div>
                      <div>
                        <h3 style={{ color: 'white', fontSize: 20, fontWeight: 600, margin: 0, marginBottom: 4 }}>
                          {langData.language}
                        </h3>
                        <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 14, margin: 0 }}>
                          {langData.keywords.length} keywords • {langData.country}
                          {langData.translatedSeed && (
                            <span style={{ 
                              marginLeft: 10,
                              color: 'rgba(255, 255, 255, 0.5)',
                              fontSize: 12
                            }}>
                              (seed: "{langData.translatedSeed}")
                            </span>
                          )}
                          {langData.realData && (
                            <span style={{ 
                              marginLeft: 10,
                              background: 'rgba(16, 185, 129, 0.2)',
                              color: '#10b981',
                              padding: '2px 8px',
                              borderRadius: 6,
                              fontSize: 11,
                              fontWeight: 600
                            }}>
                              ✓ Real Google Ads Data
                            </span>
                          )}
                          {!langData.realData && langData.aiGenerated && (
                            <span style={{ 
                              marginLeft: 10,
                              background: 'rgba(139, 92, 246, 0.2)',
                              color: '#a78bfa',
                              padding: '2px 8px',
                              borderRadius: 6,
                              fontSize: 11,
                              fontWeight: 600
                            }}>
                              ✨ AI-Generated
                            </span>
                          )}
                          {!langData.realData && langData.simulated && (
                            <span style={{ 
                              marginLeft: 10,
                              background: 'rgba(245, 158, 11, 0.2)',
                              color: '#fbbf24',
                              padding: '2px 8px',
                              borderRadius: 6,
                              fontSize: 11
                            }}>
                              Smart Suggestions
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp size={24} style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                    ) : (
                      <ChevronDown size={24} style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                    )}
                  </div>

                  {/* Keywords Table */}
                  {isExpanded && (
                    <div style={{ padding: '0 20px 20px' }}>
                      {/* Action Buttons */}
                      {getSelectedCount(lang.code) > 0 && (
                        <div style={{
                          background: 'rgba(139, 92, 246, 0.1)',
                          border: '1px solid rgba(139, 92, 246, 0.3)',
                          borderRadius: 12,
                          padding: 16,
                          marginBottom: 16,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div style={{ color: 'white', fontSize: 14 }}>
                            <strong>{getSelectedCount(lang.code)}</strong> keyword{getSelectedCount(lang.code) !== 1 ? 's' : ''} selected
                          </div>
                          <div style={{ display: 'flex', gap: 10 }}>
                            <button
                              onClick={() => clearSelection()}
                              style={{
                                padding: '8px 16px',
                                background: 'rgba(255, 255, 255, 0.1)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: 8,
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 500
                              }}
                            >
                              Clear Selection
                            </button>
                            <button
                              onClick={() => openAddToExisting(lang.code)}
                              style={{
                                padding: '8px 20px',
                                background: 'rgba(99, 102, 241, 0.15)',
                                border: '1px solid rgba(99, 102, 241, 0.3)',
                                borderRadius: 8,
                                color: '#a5b4fc',
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6
                              }}
                            >
                              <Upload size={16} />
                              Add to Existing ({getSelectedCount(lang.code)})
                            </button>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>Default:</span>
                              <select
                                value={defaultMatchType}
                                onChange={(e) => setDefaultMatchType(e.target.value)}
                                style={{
                                  background: 'rgba(255, 255, 255, 0.08)',
                                  border: '1px solid rgba(255, 255, 255, 0.15)',
                                  borderRadius: 6,
                                  color: 'white',
                                  padding: '6px 10px',
                                  fontSize: 12,
                                  cursor: 'pointer'
                                }}
                              >
                                <option value="BROAD" style={{ background: '#1a1a2e' }}>Broad</option>
                                <option value="PHRASE" style={{ background: '#1a1a2e' }}>Phrase</option>
                                <option value="EXACT" style={{ background: '#1a1a2e' }}>Exact</option>
                              </select>
                            </div>
                            <button
                              onClick={() => handleCreateCampaign(lang.code)}
                              style={{
                                padding: '8px 20px',
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                                border: 'none',
                                borderRadius: 8,
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6
                              }}
                            >
                              <Plus size={16} />
                              Create New Campaign ({getSelectedCount(lang.code)})
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                              <th style={{ 
                                textAlign: 'center',
                                padding: '12px 8px',
                                width: 40
                              }}>
                                <button
                                  onClick={() => toggleSelectAll(lang.code, langData.keywords)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  title={areAllSelected(lang.code, langData.keywords) ? 'Deselect all' : 'Select all'}
                                >
                                  {areAllSelected(lang.code, langData.keywords) ? (
                                    <CheckSquare size={18} style={{ color: '#8b5cf6' }} />
                                  ) : (
                                    <Square size={18} style={{ color: 'rgba(255, 255, 255, 0.4)' }} />
                                  )}
                                </button>
                              </th>
                              <th style={{ 
                                textAlign: 'left', 
                                padding: '12px 16px',
                                color: 'rgba(255, 255, 255, 0.6)',
                                fontSize: 13,
                                fontWeight: 600
                              }}>
                                Keyword
                              </th>
                              <th style={{ textAlign: 'left', padding: '12px 16px', color: 'rgba(255, 255, 255, 0.6)', fontSize: 13, fontWeight: 600 }}>
                                Search Volume
                              </th>
                              <th style={{ textAlign: 'left', padding: '12px 16px', color: 'rgba(255, 255, 255, 0.6)', fontSize: 13, fontWeight: 600 }}>
                                CPC
                              </th>
                              <th style={{ textAlign: 'left', padding: '12px 16px', color: 'rgba(255, 255, 255, 0.6)', fontSize: 13, fontWeight: 600 }}>
                                Competition
                              </th>
                              <th style={{ textAlign: 'center', padding: '12px 16px', color: 'rgba(255, 255, 255, 0.6)', fontSize: 13, fontWeight: 600 }}>
                                Match Type
                              </th>
                              <th style={{ textAlign: 'right', padding: '12px 16px', color: 'rgba(255, 255, 255, 0.6)', fontSize: 13, fontWeight: 600 }}>
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {langData.keywords.map((keyword, index) => {
                              const selected = isKeywordSelected(lang.code, keyword);
                              return (
                              <tr key={index} style={{ 
                                borderBottom: index < langData.keywords.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
                                transition: 'background 0.2s ease',
                                background: selected ? 'rgba(139, 92, 246, 0.08)' : 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                if (!selected) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = selected ? 'rgba(139, 92, 246, 0.08)' : 'transparent';
                              }}
                              >
                                <td style={{ padding: '14px 8px', textAlign: 'center' }}>
                                  <button
                                    onClick={() => toggleKeywordSelection(lang.code, keyword)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    {selected ? (
                                      <CheckSquare size={18} style={{ color: '#8b5cf6' }} />
                                    ) : (
                                      <Square size={18} style={{ color: 'rgba(255, 255, 255, 0.3)' }} />
                                    )}
                                  </button>
                                </td>
                                <td style={{ padding: '14px 16px', color: 'white', fontSize: 14, fontWeight: 500 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    {keyword.keyword}
                                    {getExistingInfo(keyword.keyword) && (
                                      <span
                                        title={getExistingInfo(keyword.keyword).map(e => `${e.campaign} → ${e.adGroup} (${e.matchType})`).join('\n')}
                                        style={{
                                          background: 'rgba(245, 158, 11, 0.15)',
                                          color: '#f59e0b',
                                          padding: '2px 8px',
                                          borderRadius: 8,
                                          fontSize: 10,
                                          fontWeight: 600,
                                          whiteSpace: 'nowrap',
                                          cursor: 'help'
                                        }}
                                      >
                                        Already in account
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td style={{ padding: '14px 16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: 14 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <TrendingUp size={16} style={{ color: '#10b981' }} />
                                    {keyword.volume == null ? '–' : formatNumber(keyword.volume)}
                                  </div>
                                </td>
                                <td style={{ padding: '14px 16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: 14 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ 
                                      color: '#f59e0b', 
                                      fontSize: 16, 
                                      fontWeight: 600 
                                    }}>€</span>
                                    {keyword.cpc == null ? '–' : formatNumber(keyword.cpc, 2)}
                                  </div>
                                </td>
                                <td style={{ padding: '14px 16px' }}>
                                  <span style={{
                                    background: `${getCompetitionColor(keyword.competition)}20`,
                                    color: getCompetitionColor(keyword.competition),
                                    padding: '4px 12px',
                                    borderRadius: 12,
                                    fontSize: 12,
                                    fontWeight: 600
                                  }}>
                                    {keyword.competition === 'UNKNOWN' || !keyword.competition ? 'No data' : keyword.competition}
                                  </span>
                                </td>
                                <td style={{ padding: '14px 8px', textAlign: 'center' }}>
                                  <select
                                    value={getMatchType(lang.code, keyword.keyword)}
                                    onChange={(e) => setMatchType(lang.code, keyword.keyword, e.target.value)}
                                    style={{
                                      background: 'rgba(255, 255, 255, 0.08)',
                                      border: '1px solid rgba(255, 255, 255, 0.15)',
                                      borderRadius: 6,
                                      color: 'white',
                                      padding: '4px 8px',
                                      fontSize: 12,
                                      cursor: 'pointer',
                                      fontWeight: 500
                                    }}
                                  >
                                    <option value="BROAD" style={{ background: '#1a1a2e' }}>Broad</option>
                                    <option value="PHRASE" style={{ background: '#1a1a2e' }}>Phrase</option>
                                    <option value="EXACT" style={{ background: '#1a1a2e' }}>Exact</option>
                                  </select>
                                </td>
                                <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                                  <button 
                                    onClick={() => toggleKeywordSelection(lang.code, keyword)}
                                    style={{
                                      background: selected ? 'rgba(139, 92, 246, 0.2)' : 'rgba(99, 102, 241, 0.15)',
                                      border: selected ? '1px solid rgba(139, 92, 246, 0.5)' : '1px solid rgba(99, 102, 241, 0.3)',
                                      color: selected ? '#a78bfa' : '#a5b4fc',
                                      padding: '6px 14px',
                                      borderRadius: 8,
                                      cursor: 'pointer',
                                      fontSize: 12,
                                      fontWeight: 600
                                    }}
                                  >
                                    {selected ? '✓ Selected' : 'Select'}
                                  </button>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!multiLangResults && !loading && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 20,
          padding: 60,
          textAlign: 'center',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Globe size={64} style={{ color: 'rgba(255, 255, 255, 0.3)', margin: '0 auto 20px' }} />
          <h3 style={{ color: 'white', fontSize: 20, fontWeight: 600, marginBottom: 10 }}>
            Ready to Discover Keywords
          </h3>
          <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 14, maxWidth: 400, margin: '0 auto' }}>
            Enter a seed keyword above to research keywords across multiple languages and markets simultaneously.
          </p>
        </div>
      )}

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 20
          }}
          onClick={closeLanguageModal}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: 24,
              padding: 40,
              maxWidth: 800,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ marginBottom: 30 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h2 style={{ color: 'white', fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Globe size={28} style={{ color: '#10b981' }} />
                  Select Target Languages
                </h2>
                <button
                  onClick={closeLanguageModal}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: 10,
                    width: 36,
                    height: 36,
                    color: 'rgba(255, 255, 255, 0.6)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    fontWeight: 600
                  }}
                >
                  ×
                </button>
              </div>
              <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0, fontSize: 14 }}>
                Choose which languages and markets you want to research. Selected: {tempSelectedLanguages.length} / {allLanguages.length}
              </p>
            </div>

            {/* Select All / Deselect All */}
            <div style={{ marginBottom: 20 }}>
              <button
                onClick={toggleAllTempLanguages}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: 10,
                  color: '#10b981',
                  fontSize: 13,
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                {tempSelectedLanguages.length === allLanguages.length ? '☑ Deselect All' : '☐ Select All'}
              </button>
            </div>

            {/* Language Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', 
              gap: 12,
              marginBottom: 30
            }}>
              {allLanguages.map(lang => {
                const isSelected = tempSelectedLanguages.includes(lang.code);
                return (
                  <button
                    key={lang.code}
                    onClick={() => toggleTempLanguageSelection(lang.code)}
                    disabled={isSelected && tempSelectedLanguages.length === 1}
                    style={{
                      padding: '14px 16px',
                      background: isSelected 
                        ? 'rgba(16, 185, 129, 0.2)' 
                        : 'rgba(255, 255, 255, 0.05)',
                      border: `2px solid ${isSelected ? '#10b981' : 'rgba(255, 255, 255, 0.1)'}`,
                      borderRadius: 12,
                      color: isSelected ? '#10b981' : 'rgba(255, 255, 255, 0.8)',
                      cursor: (isSelected && tempSelectedLanguages.length === 1) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      fontSize: 14,
                      fontWeight: 500,
                      transition: 'all 0.2s',
                      position: 'relative',
                      opacity: (isSelected && tempSelectedLanguages.length === 1) ? 0.6 : 1
                    }}
                  >
                    <span style={{ fontSize: 22 }}>{lang.flag}</span>
                    <span style={{ flex: 1, textAlign: 'left' }}>{lang.name}</span>
                    {isSelected ? (
                      <CheckSquare size={18} />
                    ) : (
                      <Square size={18} style={{ opacity: 0.4 }} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 20, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <button
                onClick={closeLanguageModal}
                style={{
                  padding: '12px 24px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 12,
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={applyLanguageSelection}
                style={{
                  padding: '12px 32px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: 12,
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
              >
                Apply Selection ({tempSelectedLanguages.length})
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add to Existing Campaign Modal */}
      {showAddToExistingModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e1b4b 0%, #1a1a2e 100%)',
            borderRadius: 16,
            border: '1px solid rgba(139, 92, 246, 0.2)',
            padding: 32,
            width: '90%',
            maxWidth: 520
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ color: 'white', fontSize: 18, fontWeight: 700, margin: 0 }}>
                Add Keywords to Existing Campaign
              </h3>
              <button onClick={() => { setShowAddToExistingModal(false); setAddResult(null); }} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: '0 0 20px 0' }}>
              {getSelectedCount(addToExistingLang)} keywords selected
            </p>

            {addResult ? (
              <div style={{
                background: addResult.success ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                border: `1px solid ${addResult.success ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                borderRadius: 12,
                padding: 20,
                textAlign: 'center',
                marginBottom: 20
              }}>
                {addResult.success ? <Check size={32} style={{ color: '#10b981', marginBottom: 8 }} /> : <AlertCircle size={32} style={{ color: '#ef4444', marginBottom: 8 }} />}
                <p style={{ color: addResult.success ? '#10b981' : '#ef4444', fontSize: 14, fontWeight: 600, margin: 0 }}>
                  {addResult.message}
                </p>
              </div>
            ) : (
              <>
                {/* Campaign selector with search */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    Campaign {!campaignsLoading && campaigns.length > 0 && `(${campaigns.length})`}
                  </label>
                  <input
                    type="text"
                    placeholder={campaignsLoading ? 'Loading campaigns...' : 'Search campaigns...'}
                    value={campaignSearch}
                    onChange={(e) => setCampaignSearch(e.target.value)}
                    disabled={campaignsLoading}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 13,
                      boxSizing: 'border-box',
                      marginBottom: 8
                    }}
                  />
                  {selectedCampaignId && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                      background: 'rgba(139, 92, 246, 0.15)', border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: 8, padding: '8px 12px'
                    }}>
                      <Check size={14} style={{ color: '#a78bfa' }} />
                      <span style={{ color: '#a78bfa', fontSize: 13, fontWeight: 600, flex: 1 }}>
                        {campaigns.find(c => c.id === selectedCampaignId)?.name}
                      </span>
                      <button onClick={() => { setSelectedCampaignId(''); setAdGroups([]); setSelectedAdGroupId(''); }}
                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 0 }}>
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  {!selectedCampaignId && !campaignsLoading && (
                    <div style={{
                      maxHeight: 160, overflowY: 'auto', borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)'
                    }}>
                      {campaigns
                        .filter(c => !campaignSearch || c.name?.toLowerCase().includes(campaignSearch.toLowerCase()))
                        .slice(0, 50)
                        .map(c => (
                          <div
                            key={c.id}
                            onClick={() => { handleCampaignSelect(c.id); setCampaignSearch(''); }}
                            style={{
                              padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'rgba(255,255,255,0.8)',
                              borderBottom: '1px solid rgba(255,255,255,0.05)',
                              ':hover': { background: 'rgba(139, 92, 246, 0.1)' }
                            }}
                            onMouseEnter={(e) => e.target.style.background = 'rgba(139, 92, 246, 0.15)'}
                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                          >
                            {c.name}
                          </div>
                        ))}
                      {campaigns.filter(c => !campaignSearch || c.name?.toLowerCase().includes(campaignSearch.toLowerCase())).length === 0 && (
                        <div style={{ padding: '12px', color: 'rgba(255,255,255,0.4)', fontSize: 13, textAlign: 'center' }}>
                          No campaigns found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Ad Group selector */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Ad Group</label>
                  <select
                    value={selectedAdGroupId}
                    onChange={(e) => setSelectedAdGroupId(e.target.value)}
                    disabled={!selectedCampaignId || adGroupsLoading}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: '#1a1a2e',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 13
                    }}
                  >
                    <option value="" style={{ background: '#1a1a2e', color: 'white' }}>{adGroupsLoading ? 'Loading ad groups...' : selectedCampaignId ? 'Select an ad group' : 'Select a campaign first'}</option>
                    {adGroups.map(ag => (
                      <option key={ag.id} value={ag.id} style={{ background: '#1a1a2e', color: 'white' }}>{ag.name || 'Unknown'}</option>
                    ))}
                  </select>
                </div>

                {/* Match Type selector */}
                <div style={{ marginBottom: 24 }}>
                  <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Match Type</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {['BROAD', 'PHRASE', 'EXACT'].map(mt => (
                      <button
                        key={mt}
                        onClick={() => setSelectedMatchType(mt)}
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          background: selectedMatchType === mt ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${selectedMatchType === mt ? 'rgba(139, 92, 246, 0.5)' : 'rgba(255,255,255,0.15)'}`,
                          borderRadius: 8,
                          color: selectedMatchType === mt ? '#a78bfa' : 'rgba(255,255,255,0.6)',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600
                        }}
                      >
                        {mt.charAt(0) + mt.slice(1).toLowerCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                onClick={() => { setShowAddToExistingModal(false); setAddResult(null); }}
                style={{
                  padding: '10px 24px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 8,
                  color: 'rgba(255,255,255,0.7)',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600
                }}
              >
                {addResult ? 'Close' : 'Cancel'}
              </button>
              {!addResult && (
                <button
                  onClick={handleAddKeywords}
                  disabled={!selectedCampaignId || !selectedAdGroupId || addingKeywords}
                  style={{
                    padding: '10px 24px',
                    background: (!selectedCampaignId || !selectedAdGroupId) ? 'rgba(139, 92, 246, 0.2)' : 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                    border: 'none',
                    borderRadius: 8,
                    color: (!selectedCampaignId || !selectedAdGroupId) ? 'rgba(255,255,255,0.3)' : 'white',
                    cursor: (!selectedCampaignId || !selectedAdGroupId) ? 'not-allowed' : 'pointer',
                    fontSize: 13,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  {addingKeywords ? <RefreshCw size={16} className="spin" /> : <Upload size={16} />}
                  {addingKeywords ? 'Adding...' : 'Add Keywords'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
